import {
  ButtonIcon,
  FontAwesomeIcon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { type KeyboardEvent, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

export interface AppliedFilterSummaryItem {
  label: string;
  value?: string;
}

export interface AppliedFiltersChipProps {
  count: number;
  summary: AppliedFilterSummaryItem[];
  onClear: () => void;
  onClick?: () => void;
}

export function AppliedFiltersChip({ count, summary, onClear, onClick }: AppliedFiltersChipProps) {
  const { t } = useTranslation();

  if (count <= 0) return null;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      onClick &&
      event.target === event.currentTarget &&
      (event.key === "Enter" || event.key === " ")
    ) {
      event.preventDefault();
      onClick();
    }
  };

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClear();
  };

  const chip = (
    <Stack
      direction="horizontal"
      items="center"
      gap="xs"
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      className="flex flex-row items-center gap-(--gap-xs) h-7 cursor-pointer rounded-xs bg-dark/5 pl-2 pr-1 hover:bg-dark/10"
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <Text className="whitespace-nowrap">{t("plurals:filters_applied_count", { count })}</Text>
      <ButtonIcon
        variant="plain"
        size="sm"
        icon={<FontAwesomeIcon icon="fa-light fa-times" />}
        onClick={handleClear}
      />
    </Stack>
  );

  return (
    <Tooltip>
      <TooltipTrigger>{chip}</TooltipTrigger>
      <TooltipContent>
        <Stack className={"text-sm"} gap={"xs"}>
          {summary.map((item) => (
            <Text key={item.label}>
              <b>{item.label}</b>
              {item.value ? `: ${item.value}` : ""}
            </Text>
          ))}
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
}
